import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from '@mlm/ui';
import {
  createDistributionRun,
  discardDistributionRun,
  distributeRun,
  distributionRunResults,
  getDistributionRun,
  isPreviewPending,
  listDistributionRuns,
  previewDistributionRun,
  type DistributionBreakdown,
  type DistributionRun,
  type DistributionRunPreview,
  type DistributionRunResultRow,
  type OlderOpenRun,
  type OverlappingRun,
} from '../lib/api';

/** "May payout (06/01 → 06/23), distributed 06/15" — names a conflicting run. */
function describeRun(r: OverlappingRun): string {
  const window = `${new Date(r.startAt).toLocaleDateString()} → ${new Date(r.endAt).toLocaleDateString()}`;
  const label = r.label ? `${r.label} (${window})` : window;
  const when = r.distributedAt ? `, distributed ${new Date(r.distributedAt).toLocaleString()}` : '';
  return `${label}${when}`;
}

/**
 * Per-plan ad-hoc distribution runs (Phase 1: G15). An admin defines a
 * `[start, end)` window for one plan, previews the dry-run, then distributes.
 * Preview and distribute run in the BACKGROUND (202 + transient
 * `computing` / `distributing` status) — the detail card polls the run until
 * it lands on a terminal state. Idempotent: the run's stable event id means
 * a re-fire pays nothing twice. PV qualification is summed from `pv_history`
 * over the window, independent of the monthly period clock.
 */
const RUN_PLANS = [
  { code: 'monthly_direct_bonus', label: 'Monthly Direct Bonus (G15)' },
  { code: 'stairstep', label: 'Stairstep' },
  { code: 'unilevel_consecutive_bonus', label: 'Unilevel Consecutive Bonus' },
  { code: 'binary', label: 'Binary (Teslab)' },
  { code: 'maintenance_cashback', label: 'Cashback Bonus' },
];

const IN_FLIGHT: Array<DistributionRun['status']> = ['computing', 'distributing'];

/**
 * Plans whose runs spend state that carries over (binary banks leg points).
 * Mirrors the API's `isSequentialRunPlan`: such a run pays the legs as they
 * stood at its end date, can only be distributed once that date has passed,
 * goes out oldest first, and pays exactly the figures the admin reviewed.
 */
const SEQUENTIAL_PLANS = new Set(['binary']);

function statusVariant(s: DistributionRun['status']) {
  if (s === 'distributed') return 'secondary' as const;
  if (s === 'failed') return 'destructive' as const;
  if (IN_FLIGHT.includes(s)) return 'default' as const;
  return 'outline' as const;
}

export function DistributionRunsPage() {
  const qc = useQueryClient();
  const runsQ = useQuery({
    queryKey: ['distribution-runs'],
    queryFn: () => listDistributionRuns(),
    // Keep the list live while any run has a background job in flight.
    refetchInterval: (q) =>
      (q.state.data ?? []).some((r) => IN_FLIGHT.includes(r.status)) ? 4000 : false,
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['distribution-runs'] });
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">Distribution Runs</h1>
      <p className="text-sm text-muted-foreground max-w-2xl">
        Distribute a single comp plan over a custom date window, on its own schedule — separate from
        the monthly period close. Qualification PV is summed from the points history over the
        window. Distributing is idempotent: re-running the same run never pays twice.
      </p>

      <CreateRunCard
        onCreated={(r) => {
          invalidate();
          setSelectedId(r.id);
        }}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Runs</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {(runsQ.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No distribution runs yet.</p>
          ) : (
            (runsQ.data ?? []).map((r) => (
              <button
                key={r.id}
                onClick={() => setSelectedId(r.id === selectedId ? null : r.id)}
                className={`w-full text-left flex items-center gap-3 flex-wrap rounded-md border px-3 py-2 text-sm transition-colors hover:bg-muted ${
                  r.id === selectedId ? 'border-primary/40 bg-primary/5' : ''
                }`}
              >
                <Badge variant="outline">{r.planCode}</Badge>
                <span className="font-mono text-xs text-muted-foreground">
                  {new Date(r.startAt).toLocaleDateString()} →{' '}
                  {new Date(r.endAt).toLocaleDateString()}
                </span>
                {r.label && <span className="text-muted-foreground">{r.label}</span>}
                <Badge variant={statusVariant(r.status)} className="ml-auto">
                  {r.status}
                </Badge>
              </button>
            ))
          )}
        </CardContent>
      </Card>

      {(() => {
        const selected = selectedId
          ? (runsQ.data ?? []).find((r) => r.id === selectedId)
          : undefined;
        return selected ? (
          <RunDetail
            key={selected.id}
            run={selected}
            onChanged={invalidate}
            onDiscarded={() => {
              invalidate();
              setSelectedId(null);
            }}
          />
        ) : null;
      })()}
    </div>
  );
}

function CreateRunCard({ onCreated }: { onCreated: (r: DistributionRun) => void }) {
  const [planCode, setPlanCode] = useState(RUN_PLANS[0]!.code);
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [overlap, setOverlap] = useState<OverlappingRun[]>([]);

  // Binary (Teslab) pairs at distribution time. Its rates (pairing % per
  // membership, mentor % per tier × level) live in the ComPlan config now, so
  // the run itself takes only the date window — like every other plan.
  const isBinary = planCode === 'binary';

  const mut = useMutation({
    meta: { successMessage: 'Distribution run created.' },
    mutationFn: () =>
      createDistributionRun({
        planCode,
        // Uniform full-day windows: start at 00:00:00, end at 23:59:59.999 of
        // the chosen dates (local time → ISO). The `T..` suffix forces local
        // interpretation; a bare `YYYY-MM-DD` would parse as UTC midnight.
        startAt: new Date(`${startAt}T00:00:00`).toISOString(),
        endAt: new Date(`${endAt}T23:59:59.999`).toISOString(),
        label: label || undefined,
      }),
    onSuccess: (r) => {
      onCreated(r);
      setStartAt('');
      setEndAt('');
      setLabel('');
      setError(null);
      setOverlap(r.overlapWarning ?? []);
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { error?: string } } };
      setError((e.response?.data?.error ?? 'create_failed').replace(/_/g, ' '));
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Create distribution run</CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-5 gap-3">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Plan</label>
          <Select value={planCode} onValueChange={setPlanCode}>
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RUN_PLANS.map((p) => (
                <SelectItem key={p.code} value={p.code}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Start date (00:00)</label>
          <Input type="date" value={startAt} onChange={(e) => setStartAt(e.target.value)} />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">End date (23:59)</label>
          <Input type="date" value={endAt} onChange={(e) => setEndAt(e.target.value)} />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Label (optional)</label>
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="May payout"
          />
        </div>
        <div className="flex items-end">
          <Button
            onClick={() => mut.mutate()}
            disabled={!startAt || !endAt || mut.isPending}
            className="w-full"
          >
            {mut.isPending ? 'Creating…' : 'Create'}
          </Button>
        </div>
        {isBinary && (
          <p className="md:col-span-5 text-[11px] text-muted-foreground">
            Binary (Teslab) pairs <span className="font-medium">min(left, right)</span> per slot and
            pays <span className="font-medium">paired × the tier's pairing %</span> to maintained
            members. Set the pairing % (per membership) and Mentor's Bonus % (per tier × level)
            under ComPlan → Binary → Teslab variant. A run pays only the points banked up to its end
            date (later points wait for the next run), so it can be distributed once that date has
            passed, and runs go out oldest first.
          </p>
        )}
        {error && <p className="md:col-span-5 text-xs text-destructive">{error}</p>}
        {overlap.length > 0 && (
          <div className="md:col-span-5 rounded-md border border-amber-400/50 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
            <p className="font-medium">
              Heads up: this window is already covered by a distributed run — distributing this
              draft would be blocked to prevent double-paying.
            </p>
            <ul className="mt-1 list-disc pl-4">
              {overlap.map((r) => (
                <li key={r.id}>{describeRun(r)}</li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RunDetail({
  run: listRun,
  onChanged,
  onDiscarded,
}: {
  run: DistributionRun;
  onChanged: () => void;
  onDiscarded: () => void;
}) {
  const [preview, setPreview] = useState<DistributionRunPreview | null>(null);
  const [results, setResults] = useState<DistributionRunResultRow[] | null>(null);
  const [overlapError, setOverlapError] = useState<OverlappingRun[] | null>(null);
  const [olderRuns, setOlderRuns] = useState<OlderOpenRun[] | null>(null);
  const [drift, setDrift] = useState<{ previousTotal: string | null; newTotal: string } | null>(
    null,
  );
  const [closeConflict, setCloseConflict] = useState<Array<{
    periodId: string;
    code: string;
    startAt: string;
    endAt: string;
  }> | null>(null);

  // The run's live row — polls while a background preview/distribute is in
  // flight (202 semantics). The list row is only the initial snapshot.
  const runQ = useQuery({
    queryKey: ['distribution-run', listRun.id],
    queryFn: () => getDistributionRun(listRun.id),
    initialData: listRun,
    refetchInterval: (q) =>
      q.state.data && IN_FLIGHT.includes(q.state.data.status) ? 1500 : false,
  });
  const run = runQ.data ?? listRun;

  const distributed = run.status === 'distributed';
  const distributing = run.status === 'distributing';
  const computing = run.status === 'computing';
  const sequential = SEQUENTIAL_PLANS.has(run.planCode);
  // The API refuses these until the end date has passed; say so up front.
  const windowOpen = sequential && !distributed && new Date(run.endAt).getTime() > Date.now();
  const jobError =
    run.status === 'failed' && typeof run.snapshot?.error === 'string'
      ? (run.snapshot.error as string)
      : null;

  const previewMut = useMutation({
    mutationFn: (recompute: boolean) => previewDistributionRun(run.id, recompute),
    onSuccess: (p) => {
      if (isPreviewPending(p)) {
        // 202: compute queued in the background — the poll takes over and the
        // status transition below re-fetches the stored figures when done.
        void runQ.refetch();
        onChanged();
        return;
      }
      setPreview(p);
      setOverlapError(null);
      onChanged();
    },
  });
  const distributeMut = useMutation({
    meta: {
      successMessage: 'Distribution started — running in the background.',
      // Explained inline below instead of a toast.
      silentErrorCodes: ['older_run_not_distributed', 'preview_changed'],
    },
    mutationFn: () => distributeRun(run.id),
    onSuccess: () => {
      setOverlapError(null);
      setCloseConflict(null);
      setOlderRuns(null);
      setDrift(null);
      void runQ.refetch();
      onChanged();
    },
    onError: (err: unknown) => {
      const e = err as {
        response?: {
          data?: {
            error?: string;
            runs?: OverlappingRun[] | OlderOpenRun[];
            periods?: Array<{ periodId: string; code: string; startAt: string; endAt: string }>;
            previousTotal?: string | null;
            newTotal?: string;
          };
        };
      };
      const data = e.response?.data;
      if (data?.error === 'overlapping_run_already_distributed') {
        setOverlapError((data.runs as OverlappingRun[] | undefined) ?? []);
      } else if (data?.error === 'period_close_already_paid_plan') {
        setCloseConflict(data.periods ?? []);
      } else if (data?.error === 'older_run_not_distributed') {
        setOlderRuns((data.runs as OlderOpenRun[] | undefined) ?? []);
      } else if (data?.error === 'preview_changed') {
        // The API stored the fresh figures on the run; show them for review.
        setDrift({ previousTotal: data.previousTotal ?? null, newTotal: data.newTotal ?? '0' });
        void runQ.refetch();
        previewMut.mutate(false);
        onChanged();
      }
    },
  });
  const discardMut = useMutation({
    meta: { successMessage: 'Run discarded.' },
    mutationFn: () => discardDistributionRun(run.id),
    onSuccess: () => onDiscarded(),
  });
  const resultsMut = useMutation({
    mutationFn: () => distributionRunResults(run.id),
    onSuccess: (r) => setResults(r),
  });

  // Detect the background job's terminal transition and finish the flow the
  // old synchronous handlers used to: distributed → toast + load results;
  // computed → re-fetch the stored preview figures; failed → error toast.
  const prevStatus = useRef(run.status);
  useEffect(() => {
    const prev = prevStatus.current;
    prevStatus.current = run.status;
    if (prev === run.status) return;
    if (prev === 'distributing' && run.status === 'distributed') {
      toast.success('Distribution run completed.');
      onChanged();
      resultsMut.mutate();
    } else if (prev === 'computing' && run.status === 'computed') {
      // Short-circuits to the stored snapshot + overlap warning.
      previewMut.mutate(false);
    } else if (IN_FLIGHT.includes(prev) && run.status === 'failed') {
      toast.error('Background job failed — re-running is safe (idempotent).');
      onChanged();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run.status]);

  // A distributed run covering this window (from preview's non-blocking warning)
  // means distributing would be hard-blocked — disable the button proactively.
  const knownOverlap = overlapError ?? (distributed ? null : (preview?.overlapWarning ?? null));
  const blockedByOverlap = !distributed && (knownOverlap?.length ?? 0) > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2 flex-wrap">
          <Badge variant="outline">{run.planCode}</Badge>
          <span className="font-mono text-xs text-muted-foreground">{run.eventId}</span>
          <Badge variant={statusVariant(run.status)}>{run.status}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2 flex-wrap">
          <Button
            size="sm"
            variant="default"
            disabled={previewMut.isPending || computing || distributing}
            onClick={() => previewMut.mutate(false)}
          >
            {computing
              ? 'Computing…'
              : previewMut.isPending
                ? 'Loading…'
                : distributed
                  ? 'View distributed result'
                  : 'Preview'}
          </Button>
          {!distributed && (
            <Button
              size="sm"
              variant="outline"
              disabled={previewMut.isPending || computing || distributing}
              onClick={() => {
                setDrift(null);
                previewMut.mutate(true);
              }}
              title="Re-run the dry-run against current data (figures may change as other runs distribute)"
            >
              Recompute
            </Button>
          )}
          <Button
            size="sm"
            variant="destructive"
            disabled={
              distributed ||
              blockedByOverlap ||
              windowOpen ||
              distributeMut.isPending ||
              distributing ||
              computing
            }
            onClick={() => distributeMut.mutate()}
          >
            {distributing || distributeMut.isPending
              ? 'Distributing…'
              : distributed
                ? 'Distributed'
                : 'Distribute'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={resultsMut.isPending}
            onClick={() => resultsMut.mutate()}
          >
            {resultsMut.isPending ? 'Loading…' : 'View results'}
          </Button>
          {!distributed && (
            <Button
              size="sm"
              variant="ghost"
              className="ml-auto text-muted-foreground"
              disabled={discardMut.isPending || computing || distributing}
              onClick={() => {
                if (
                  window.confirm('Discard this run? It has not paid anything, so nothing is lost.')
                )
                  discardMut.mutate();
              }}
            >
              {discardMut.isPending ? 'Discarding…' : 'Discard'}
            </Button>
          )}
        </div>

        {windowOpen && (
          <p className="text-[11px] text-muted-foreground">
            This run pays the points banked up to {new Date(run.endAt).toLocaleString()}. It can be
            distributed once that time has passed, so nothing from its last day is left out.
          </p>
        )}

        {olderRuns && olderRuns.length > 0 && (
          <div className="rounded-md border border-amber-400/50 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
            <p className="font-medium">
              An older run for this plan hasn't been distributed yet. Distribute or discard it
              first, so each run pays only its own dates.
            </p>
            <ul className="mt-1 list-disc pl-4">
              {olderRuns.map((r) => (
                <li key={r.id}>
                  {r.label ? `${r.label} ` : ''}
                  {new Date(r.startAt).toLocaleDateString()} →{' '}
                  {new Date(r.endAt).toLocaleDateString()} ({r.status})
                </li>
              ))}
            </ul>
          </div>
        )}

        {drift && (
          <div className="rounded-md border border-amber-400/50 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
            <p className="font-medium">
              {drift.previousTotal === null
                ? 'These figures had not been reviewed yet, so nothing was paid.'
                : `The figures changed since they were reviewed (${drift.previousTotal} → ${drift.newTotal}), so nothing was paid.`}
            </p>
            <p className="mt-0.5">
              The current figures are shown below. Check them, then click Distribute again.
            </p>
          </div>
        )}

        {(computing || distributing) && (
          <p className="text-[11px] text-muted-foreground">
            {computing ? 'Preview is computing' : 'Distribution is running'} in the background —
            this can take several minutes on a large member base. Progress is saved server-side;
            it's safe to leave this page.
          </p>
        )}

        {jobError && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            <p className="font-medium">Background job failed:</p>
            <p className="font-mono mt-0.5">{jobError}</p>
            <p className="mt-1">Re-running is safe — already-paid entries are never paid twice.</p>
          </div>
        )}

        {knownOverlap && knownOverlap.length > 0 && (
          <div className="rounded-md border border-amber-400/50 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
            <p className="font-medium">
              This window is already covered by a distributed run — distributing is blocked to
              prevent double-paying.
            </p>
            <ul className="mt-1 list-disc pl-4">
              {knownOverlap.map((r) => (
                <li key={r.id}>{describeRun(r)}</li>
              ))}
            </ul>
          </div>
        )}

        {closeConflict && closeConflict.length > 0 && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            <p className="font-medium">
              Blocked: this plan was already paid at a monthly period close covering this window —
              distributing would double-pay.
            </p>
            <ul className="mt-1 list-disc pl-4">
              {closeConflict.map((p) => (
                <li key={p.periodId}>
                  {p.code} ({new Date(p.startAt).toLocaleDateString()} →{' '}
                  {new Date(p.endAt).toLocaleDateString()})
                </li>
              ))}
            </ul>
            <p className="mt-1">
              To distribute this plan ad-hoc, mark it distribution-run-only under Settings so the
              close stops paying it.
            </p>
          </div>
        )}

        {distributed && preview?.frozen && (
          <p className="text-[11px] text-muted-foreground">
            Figures are final — shown as distributed
            {preview.distributedAt ? ` ${new Date(preview.distributedAt).toLocaleString()}` : ''}.
          </p>
        )}

        {preview && !distributed && preview.computed && (
          <p className="text-[11px] text-muted-foreground">
            {sequential ? (
              <>
                Showing the run's stored figures. Distributing pays exactly these: if anything has
                changed since, it stops and shows the new figures first.
              </>
            ) : (
              <>
                Showing the run's stored figures. Distributing recalculates against current data, so
                click <span className="font-medium">Recompute</span> first if anything may have
                changed.
              </>
            )}
          </p>
        )}

        {preview && (
          <div className="space-y-1">
            <p className="text-[11px] text-muted-foreground">
              Projected payout (dry-run): <span className="font-mono">{preview.totalCash}</span>{' '}
              across {preview.recipients.length} recipient
              {preview.recipients.length === 1 ? '' : 's'}
            </p>
            <PayoutTable
              rows={preview.recipients.map((r) => ({
                slot: r.slotCode ?? r.slotId.slice(0, 8),
                owner: r.ownerName,
                kind: r.kind,
                amount: r.totalAmount,
                metadata: r.metadata,
              }))}
            />
          </div>
        )}

        {results && (
          <div className="space-y-1">
            <p className="text-[11px] text-muted-foreground">
              Ledger rows written: {results.length}
            </p>
            <PayoutTable
              rows={results.map((r) => ({
                slot: r.slotCode ?? r.slotId.slice(0, 8),
                owner: r.ownerName,
                kind: r.kind,
                amount: r.amount,
                metadata: r.metadata ?? null,
              }))}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

type PayoutRow = {
  slot: string;
  owner: string | null;
  kind: string;
  amount: string;
  metadata?: DistributionBreakdown | null;
};

/** True when a row carries a breakdown worth expanding (any plan). */
function hasBreakdown(m: DistributionBreakdown | null | undefined): boolean {
  if (!m) return false;
  return (
    (m.breakdown != null && Object.keys(m.breakdown).length > 0) ||
    (Array.isArray(m.from_sponsors) && m.from_sponsors.length > 0) ||
    m.pairing_pct != null || // binary
    m.rank != null || // stairstep
    m.ppv != null || // maintenance cashback
    m.streak_months != null // unilevel consecutive
  );
}

function PayoutTable({ rows }: { rows: PayoutRow[] }) {
  const [open, setOpen] = useState<Set<number>>(new Set());
  if (rows.length === 0) {
    return <p className="text-[11px] text-muted-foreground">Nothing to pay for this window.</p>;
  }
  const anyExpandable = rows.some((r) => hasBreakdown(r.metadata));
  const toggle = (i: number) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  return (
    <div className="overflow-x-auto border rounded-md max-h-72 overflow-y-auto">
      <table className="w-full text-xs">
        <thead className="bg-muted/40 text-left text-muted-foreground sticky top-0">
          <tr>
            {anyExpandable && <th className="w-6 px-1 py-1.5"></th>}
            <th className="px-2 py-1.5 font-medium">Recipient slot</th>
            <th className="px-2 py-1.5 font-medium">Owner</th>
            <th className="px-2 py-1.5 font-medium">Kind</th>
            <th className="px-2 py-1.5 font-medium text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const expandable = hasBreakdown(r.metadata);
            const isOpen = open.has(i);
            return (
              <Fragment key={i}>
                <tr
                  className={`border-t ${expandable ? 'cursor-pointer hover:bg-muted/30' : ''}`}
                  onClick={expandable ? () => toggle(i) : undefined}
                >
                  {anyExpandable && (
                    <td className="px-1 py-1 text-center text-muted-foreground">
                      {expandable ? (isOpen ? '▾' : '▸') : ''}
                    </td>
                  )}
                  <td className="px-2 py-1 font-mono">{r.slot}</td>
                  <td className="px-2 py-1 text-muted-foreground">{r.owner ?? ''}</td>
                  <td className="px-2 py-1 text-muted-foreground">{r.kind}</td>
                  <td className="px-2 py-1 text-right font-mono">{Number(r.amount).toFixed(2)}</td>
                </tr>
                {expandable && isOpen && (
                  <tr className="bg-muted/20">
                    <td colSpan={anyExpandable ? 5 : 4} className="px-3 py-2">
                      <BreakdownDetail metadata={r.metadata!} amount={r.amount} kind={r.kind} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** A simple label → value list used by the non-G15 breakdowns. */
function FactList({ rows }: { rows: Array<[string, string]> }) {
  return (
    <table className="text-[11px]">
      <tbody>
        {rows.map(([label, value]) => (
          <tr key={label}>
            <td className="py-0.5 pr-3 text-muted-foreground">{label}</td>
            <td className="py-0.5 font-mono">{value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Heading({ children }: { children: ReactNode }) {
  return (
    <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">{children}</p>
  );
}

/** Renders "how the amount was computed" per plan, from the entry metadata. */
function BreakdownDetail({
  metadata,
  amount,
  kind,
}: {
  metadata: DistributionBreakdown;
  amount: string;
  kind: string;
}) {
  // Binary (Teslab) pairing.
  if (kind === 'binary_pairing' && metadata.pairing_pct != null) {
    const rows: Array<[string, string]> = [
      ['Paired points (min of legs)', Number(metadata.paired_points ?? 0).toFixed(2)],
      ['Pairing rate', `${Number(metadata.pairing_pct ?? 0)}%`],
      ['PV consumed (each leg)', Number(metadata.pv_consumed ?? 0).toFixed(2)],
    ];
    if (Number(metadata.cap_flushed ?? 0) > 0)
      rows.push(['Cap took (above per-distribution cap)', Number(metadata.cap_flushed).toFixed(2)]);
    if (Number(metadata.flushed_pv ?? 0) > 0)
      rows.push(['Strong-leg PV flushed', Number(metadata.flushed_pv).toFixed(2)]);
    rows.push(['Pairing income', Number(amount).toFixed(2)]);
    return (
      <div>
        <Heading>How this was computed · Binary pairing</Heading>
        <FactList rows={rows} />
      </div>
    );
  }

  // Stairstep differential commission.
  if (kind === 'stairstep_commission' && metadata.rank != null) {
    const payerPct = Number(metadata.rank_pct ?? 0);
    const legs = Array.isArray(metadata.legs) ? metadata.legs : [];
    // Same-rank royalty earned across all breakaway legs, surfaced as its own
    // summary row so it isn't hidden inside the per-leg table.
    const royaltyLegs = legs.filter((l) => l.royalty === true);
    const royaltyTotal = royaltyLegs.reduce((sum, l) => sum + Number(l.amount ?? 0), 0);
    return (
      <div>
        <Heading>How this was computed · Stairstep</Heading>
        <FactList
          rows={[
            ['Rank', String(metadata.rank)],
            ['Rank rate', `${payerPct}%`],
            ['Own PPV', Number(metadata.personal_pv ?? 0).toFixed(2)],
            ['Group PV (downline)', Number(metadata.group_pv ?? 0).toFixed(2)],
            ...(royaltyLegs.length > 0
              ? [
                  [
                    `Royalty (same-rank, ${royaltyLegs.length} leg${royaltyLegs.length > 1 ? 's' : ''})`,
                    royaltyTotal.toFixed(2),
                  ] as [string, string],
                ]
              : []),
            ['Commission', Number(amount).toFixed(2)],
          ]}
        />
        {legs.length > 0 && (
          <div className="mt-2">
            <Heading>Per-leg breakdown (breakaway compression)</Heading>
            <table className="text-[11px]">
              <thead>
                <tr className="text-muted-foreground">
                  <th className="py-0.5 pr-3 text-left font-normal">Slot</th>
                  <th className="py-0.5 pr-3 text-right font-normal">Its rank %</th>
                  <th className="py-0.5 pr-3 text-right font-normal">Passed through %</th>
                  <th className="py-0.5 pr-3 text-right font-normal">Rate applied</th>
                  <th className="py-0.5 pr-3 text-right font-normal">PV</th>
                  <th className="py-0.5 text-right font-normal">Amount</th>
                </tr>
              </thead>
              <tbody>
                {legs.map((leg, i) => {
                  const pathMax = Number(leg.path_max_pct ?? 0);
                  // A same-rank ROYALTY leg pays `royalty_pct × group PV` instead
                  // of the differential (which is 0 once the leg has broken away),
                  // so its applied rate is the royalty %, not payerPct − pathMax.
                  const isRoyalty = leg.royalty === true;
                  const applied = isRoyalty
                    ? Number(leg.royalty_pct ?? 0)
                    : Math.max(0, payerPct - pathMax);
                  return (
                    <tr key={`${String(leg.slot_id)}-${i}`} className="font-mono">
                      <td className="py-0.5 pr-3">
                        {leg.self ? 'self' : (leg.slot_code ?? String(leg.slot_id).slice(0, 8))}
                        {isRoyalty && (
                          <span className="ml-1.5 rounded bg-amber-100 px-1 py-px text-[9px] font-medium uppercase tracking-wide text-amber-700">
                            royalty
                          </span>
                        )}
                      </td>
                      <td className="py-0.5 pr-3 text-right">{Number(leg.pct ?? 0)}%</td>
                      <td className="py-0.5 pr-3 text-right">{isRoyalty ? '—' : `${pathMax}%`}</td>
                      <td className="py-0.5 pr-3 text-right">{applied}%</td>
                      <td className="py-0.5 pr-3 text-right">{Number(leg.ppv ?? 0).toFixed(2)}</td>
                      <td className="py-0.5 text-right">{Number(leg.amount ?? 0).toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // Maintenance cashback (% of own PPV).
  if (kind === 'maintenance_cashback' && metadata.ppv != null) {
    return (
      <div>
        <Heading>How this was computed · Cashback</Heading>
        <FactList
          rows={[
            ['Personal PV (PPV)', Number(metadata.ppv ?? 0).toFixed(2)],
            ['Min PPV (maintenance)', Number(metadata.min_ppv ?? 0).toFixed(2)],
            ['Cashback rate', `${Number(metadata.percentage ?? 0)}%`],
            ['Cashback', Number(amount).toFixed(2)],
          ]}
        />
      </div>
    );
  }

  // Unilevel consecutive streak bonus.
  if (kind === 'unilevel_consecutive_bonus' && metadata.streak_months != null) {
    return (
      <div>
        <Heading>How this was computed · Consecutive bonus</Heading>
        <FactList
          rows={[
            ['Consecutive months', String(metadata.streak_months)],
            ['PPV threshold / month', Number(metadata.threshold ?? 0).toFixed(2)],
            ['Bonus', Number(amount).toFixed(2)],
          ]}
        />
      </div>
    );
  }

  const breakdown = metadata.breakdown;
  if (breakdown && Object.keys(breakdown).length > 0) {
    const tiers = Object.entries(breakdown);
    return (
      <div className="space-y-1">
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
          How this was computed{' '}
          {metadata.total_directs != null && (
            <span className="normal-case">· {metadata.total_directs} qualifying direct(s)</span>
          )}
        </p>
        <table className="w-full text-[11px]">
          <thead className="text-muted-foreground">
            <tr>
              <th className="py-0.5 pr-3 text-left font-medium">Direct tier</th>
              <th className="py-0.5 pr-3 text-right font-medium">Directs</th>
              <th className="py-0.5 pr-3 text-right font-medium">Points each</th>
              <th className="py-0.5 text-right font-medium">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {tiers.map(([tier, b]) => (
              <tr key={tier}>
                <td className="py-0.5 pr-3 font-mono">{tier}</td>
                <td className="py-0.5 pr-3 text-right">{b.count}</td>
                <td className="py-0.5 pr-3 text-right">{b.points}</td>
                <td className="py-0.5 text-right font-mono">{Number(b.bonus).toFixed(2)}</td>
              </tr>
            ))}
            <tr className="border-t font-medium">
              <td className="py-0.5 pr-3" colSpan={3}>
                Total
              </td>
              <td className="py-0.5 text-right font-mono">{Number(amount).toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }
  if (Array.isArray(metadata.from_sponsors) && metadata.from_sponsors.length > 0) {
    return (
      <p className="text-[11px] text-muted-foreground">
        Mentor bonus
        {metadata.mentor_bonus_percentage_source
          ? ` (${String(metadata.mentor_bonus_percentage_source).replace(/_/g, ' ')})`
          : ''}{' '}
        aggregated from {metadata.from_sponsors.length} sponsor
        {metadata.from_sponsors.length === 1 ? '' : 's'}.
      </p>
    );
  }
  return null;
}
